import React, { ChangeEvent } from "react";
import { portalPickerLabel } from "@Features/sla/slaReportModel";
import { Trans } from "@i18n";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { HiOutlineUpload } from "react-icons/hi";
import { Box, AutocompleteRenderInputParams } from "@mui/material";
import IconComponent from "@CubeComponents/icons";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import Text from "@CubeComponents/html/Text/Text";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";

type PrimitiveValue = string | number | null;
interface PortalForUser {
  _id: string;
  name: string;
  [key: string]: unknown;
}

interface UserForTicketCreation {
  _id: string;
  fullName: string;
  email: string;
  portals: PortalForUser[];
  [key: string]: unknown;
}

interface TicketDetails {
  createOnBehalfOf?: { portalId: string; userId: string } | null;
  summary: string;
  description: string;
  attachments: File[];
  priority: PrimitiveValue;
  impact: PrimitiveValue;
  link: string;
  visibleToPortalUsers: boolean;
  visibleToOrgUsers: boolean;
}

interface PriorityOption {
  id: string | number;
  [key: string]: unknown;
}

interface ImpactOption {
  id: string | number;
  label?: string;
  description?: string;
  [key: string]: unknown;
}

interface CreateOnBehalfComponentProps {
  createOnBehalfOfOtherUser: boolean;
  handleCreateOnBehalfOfChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface SelectUserComponentProps {
  selectedUser: UserForTicketCreation | null;
  handleSelectUser: (event: React.SyntheticEvent, value: UserForTicketCreation | null) => void;
  allUsers: UserForTicketCreation[];
  t: (key: string) => string;
}

interface SelectPortalComponentProps {
  handleSelectPortal: (event: React.SyntheticEvent, value: PortalForUser | null) => void;
  portals: PortalForUser[];
  selectedPortal?: PortalForUser | null;
  disabled?: boolean;
  t: (key: string) => string;
}

interface SummaryComponentProps {
  ticketDetails: TicketDetails;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

interface DescriptionComponentProps {
  maxLength: number;
  ticketDetails: TicketDetails;
  handleOnChange: (event: { target: { value: string; name: string } }) => void;
  placeholder?: string;
}

interface AttachmentComponentProps {
  handleFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  ticketDetails: TicketDetails;
  handleRemoveFile: (fileName: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  t: (key: string) => string;
}

interface PriorityComponentProps {
  priorityDataSource: PriorityOption[];
  ticketDetails: TicketDetails;
  handleOnChange: (event: { target: { value: PrimitiveValue; name: string } }) => void;
  placeholder?: string;
}

interface ImpactComponentProps {
  impactDataSource: ImpactOption[];
  ticketDetails: TicketDetails;
  placeholder?: string;
  handleOnChange: (event: { target: { value: PrimitiveValue; name: string } }) => void;
}

interface SeverityOption {
  id: string | number;
  label?: string;
  description?: string;
  [key: string]: unknown;
}

interface SeverityComponentProps {
  severityDataSource: SeverityOption[];
  ticketDetails: TicketDetails & { severity?: PrimitiveValue };
  placeholder?: string;
  handleOnChange: (event: { target: { value: PrimitiveValue; name: string } }) => void;
}

interface LinkUrlComponentProps {
  ticketDetails: TicketDetails;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

interface VisibilityComponentProps {
  handleVisibiltyChange: (event: ChangeEvent<HTMLInputElement>, field: string) => void;
  ticketDetails: TicketDetails;
}

export const CreateOnBehalfComponent = ({
  createOnBehalfOfOtherUser,
  handleCreateOnBehalfOfChange,
}: CreateOnBehalfComponentProps) => {
  return <ToggleSwitchComponent checked={createOnBehalfOfOtherUser} onChange={handleCreateOnBehalfOfChange} />;
};

export const SelectUserComponent = ({ selectedUser, handleSelectUser, allUsers, t }: SelectUserComponentProps) => {
  return (
    <AutoComplete
      options={allUsers}
      sx={{
        width: "100%",
        ".MuiOutlinedInput-root": {
          padding: "0",
        },
      }}
      value={selectedUser ?? undefined}
      valueField="_id"
      getOptionLabel={(option: UserForTicketCreation) => `${option.fullName} (${option.email})`}
      id="select-user"
      disableClearable
      onChange={handleSelectUser}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextFieldComponent {...params} placeholder={t("Select a user")} />
      )}
    />
  );
};

export const SelectPortalComponent = ({
  handleSelectPortal,
  portals,
  selectedPortal,
  disabled,
  t,
}: SelectPortalComponentProps) => {
  return (
    <AutoComplete
      options={portals}
      sx={{
        width: "100%",
        ".MuiOutlinedInput-root": {
          padding: "0px",
        },
      }}
      value={selectedPortal ?? undefined}
      valueField="_id"
      getOptionLabel={(option: PortalForUser) => portalPickerLabel(option)}
      id="select-portal"
      disableClearable
      disabled={disabled}
      onChange={handleSelectPortal}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextFieldComponent {...params} placeholder={t("Select a portal")} />
      )}
    />
  );
};

export const SummaryComponent = ({ ticketDetails, handleOnChange, placeholder }: SummaryComponentProps) => {
  return (
    <TextFieldComponent
      value={ticketDetails.summary}
      onChange={handleOnChange}
      size="small"
      name={"summary"}
      placeholder={placeholder}
      width="100%"
    />
  );
};

export const DescriptionComponent = ({ maxLength, ticketDetails, handleOnChange }: DescriptionComponentProps) => {
  return (
    <SimpleEditor
      showCharCount={true}
      maxLength={maxLength}
      showToolbar={true}
      height="150"
      allowImageUpload={true}
      uploadType="jira"
      value={sanitizeHtml(ticketDetails.description)}
      onChange={(html) => {
        handleOnChange({
          target: { name: "description", value: html },
        });
      }}
    />
  );
};

export const AttachmentComponent = ({
  handleFileSelect,
  ticketDetails,
  handleRemoveFile,
  fileInputRef,
  handleDrop,
}: AttachmentComponentProps) => {
  return (
    <div className="flex w-full flex-col gap-5">
      <Box
        className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:border-gray-500 transition"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e: { preventDefault: () => void }) => e.preventDefault()}
        onDrop={handleDrop}
        aria-label="Upload files"
        sx={{ background: "none", border: "inherit", outline: "none" }}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="w-12 h-12 border border-gray-400 rounded-full flex items-center justify-center">
            <HiOutlineUpload className="text-gray-500 text-xl" />
          </div>
          <Text className="text-sm text-gray-600">
            <Trans
              i18nKey="Drop file here or <CustomLink>click to upload</CustomLink>"
              components={{
                CustomLink: <Text className="text-black font-medium underline" />,
              }}
            />
          </Text>
        </div>
        <input
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
        />
      </Box>

      {ticketDetails.attachments.length > 0 && (
        <div className="flex flex-col gap-3">
          {ticketDetails.attachments.map((file: File) => (
            <div
              key={file.name}
              className="flex gap-3 border-solid border-2 border-button_light rounded-lg p-2 items-center"
            >
              <div className="flex object-contain">{getFileIcon(file.name)}</div>
              <div className="w-full">{file.name}</div>
              <div className="w-8">
                <IconComponent
                  className="cursor-pointer"
                  onClick={() => handleRemoveFile(file.name)}
                  name="close"
                  width="0.9375rem"
                  height="0.9375rem"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const PriorityComponent = ({
  priorityDataSource,
  ticketDetails,
  handleOnChange,
  placeholder,
}: PriorityComponentProps) => {
  return (
    <AutoComplete
      onChange={(_event: React.SyntheticEvent, newValue: PriorityOption | null) => {
        handleOnChange({ target: { value: newValue?.id || null, name: "priority" } });
      }}
      value={ticketDetails.priority != null ? String(ticketDetails.priority) : null}
      valueField="id"
      options={priorityDataSource}
      sx={{ width: "100%" }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextFieldComponent {...params} placeholder={placeholder} />
      )}
    />
  );
};

export const ImpactComponent = ({
  impactDataSource,
  ticketDetails,
  placeholder,
  handleOnChange,
}: ImpactComponentProps) => {
  return (
    <AutoComplete
      onChange={(_event: React.SyntheticEvent, newValue: ImpactOption | null) => {
        handleOnChange({ target: { value: newValue?.id || null, name: "impact" } });
      }}
      value={ticketDetails.impact != null ? String(ticketDetails.impact) : null}
      valueField="id"
      options={impactDataSource}
      sx={{ width: "100%" }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextFieldComponent {...params} placeholder={placeholder} />
      )}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ImpactOption) => (
        <li {...props} key={option.id} className="flex flex-col items-start p-4 gap-2">
          <span className="text-sm font-semibold text-primary_text">{option.label}</span>
          <span className="text-sm text-primary_text">{option.description}</span>
        </li>
      )}
    />
  );
};

export const SeverityComponent = ({
  severityDataSource,
  ticketDetails,
  placeholder,
  handleOnChange,
}: SeverityComponentProps) => {
  return (
    <AutoComplete
      onChange={(_event: React.SyntheticEvent, newValue: SeverityOption | null) => {
        handleOnChange({ target: { value: newValue?.id || null, name: "severity" } });
      }}
      value={ticketDetails.severity != null ? String(ticketDetails.severity) : null}
      valueField="id"
      options={severityDataSource}
      sx={{ width: "100%" }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextFieldComponent {...params} placeholder={placeholder} />
      )}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: SeverityOption) => (
        <li {...props} key={option.id} className="flex flex-col items-start p-4 gap-2">
          <span className="text-sm font-semibold text-primary_text">{option.label}</span>
          <span className="text-sm text-primary_text">{option.description}</span>
        </li>
      )}
    />
  );
};

export const LinkUrlComponent = ({ ticketDetails, handleOnChange, placeholder }: LinkUrlComponentProps) => {
  return (
    <TextFieldComponent
      value={ticketDetails.link}
      onChange={handleOnChange}
      size="small"
      name={"link"}
      placeholder={placeholder}
      width="100%"
    />
  );
};

export const VisibilityComponent = ({ handleVisibiltyChange, ticketDetails }: VisibilityComponentProps) => {
  return (
    <Box className="flex flex-col">
      <ToggleSwitchComponent
        checked={ticketDetails.visibleToPortalUsers}
        onChange={(args) => handleVisibiltyChange(args, "visibleToPortalUsers")}
        label="Visible to all portal users"
      />
      <ToggleSwitchComponent
        checked={ticketDetails.visibleToOrgUsers}
        onChange={(args) => handleVisibiltyChange(args, "visibleToOrgUsers")}
        label="Visible to Org users"
      />
    </Box>
  );
};
